<template>
  <div class="base-card">
    <div class="card-header" :class="{ 'spinner': spinnerOn }">
      <div class="card-name">Genomics Test Results</div>
      <div class="card-icon">
        <span class="material-symbols-outlined" @click="closeCenterColumn">close</span>
      </div>
    </div>
    <div v-if="!multiComparison" class="header-container">
      <div class="items">
        <div class="header-top-first">
          <span class="specimen-type-text">{{ specimenType || '—' }}</span>
        </div>
        <div class="header-top-second">
          <span>{{ testType || '—' }}</span>
        </div>
        <div class="header-middle">
          <!-- TODO: reenable when we have the new source <div class="items">
          <span class="label">Diagnosis Reported</span> <span>{{ diagnosis || '—' }}</span>
        </div> -->
          <div class="items msi-tmb">
            <span class="label">TMB</span> <span>{{ tmb || 'No clinically significant TMB' }}</span>
          </div>
          <div class="items msi-tmb">
            <span class="label">MSI</span> <span>{{ msi || 'No clinically significant MSI' }}</span>
          </div>
        </div>
      </div>
      <div class="view-report">
        <a v-if="trialLinkUrl" :href=trialLinkUrl target="_blank">
          <button class="primary-outlined-button report-button" @click="openLink">
            <span class="material-symbols-outlined file-icon">link</span>
            Microsoft Trials
          </button>
        </a>
        <button v-if="uuid" class="primary-outlined-button report-button" @click="openFile">
          <span class="material-symbols-outlined file-icon">file_open</span>
          View Report
        </button>
      </div>
    </div>
    <div v-else class="multi-samples">
      <div v-for="(sample, index) in sampleId" :key="sample.id" class="multi-samples-div">
        <MultiSampleCard :date="sample.date" :number="index + 1" :count="sampleId.length" :diagnosis="diagnosis"
          :lab-name="sample.source_lab" :sample-id="sample.sample" :multi-card-clicked="multiCardClicked"
          @click="onMultiComparisonCardClick(index + 1)" @mouseenter="onMultiHoverOn(index + 1)"
          @mouseleave="onMultiHoverOff" :tmb="sample.tmb" :msi="sample.msi"></MultiSampleCard>
      </div>
    </div>
    <div class="genomics-data">
      <div class="variants-container">
        <div class="tier-tabs-container">
          <div class="tier-tabs" @click="tierButtonClick(1)" :class="{ 'tier-tab-active': tier1Selected }">
            <span class="material-symbols-outlined size-20">
              radio_button_{{ tierIcon1 }}
            </span>
            <span class="tier-text"> Tier 1</span>
          </div>
          <div class="tier-tabs" @click="tierButtonClick(2)" :class="{ 'tier-tab-active': tier2Selected }">
            <span class="material-symbols-outlined size-20">
              radio_button_{{ tierIcon2 }}
            </span>
            <span class="tier-text"> Tier 2</span>
          </div>
          <div class="tier-tabs" @click="tierButtonClick(3)" :class="{ 'tier-tab-active': tier3Selected }">
            <span class="material-symbols-outlined size-20">
              radio_button_{{ tierIcon3 }}
            </span>
            <span class="tier-text"> Tier 3</span>
          </div>
        </div>
        <div class="significance-label">Alteration(s) of <span class="tier-label">{{ tierLabel }}</span> Clinical
          Significance</div>
        <div class="table-headers" :class="{ 'table-headers-multi': multiComparison }">
          <span class="alteration-label">Alteration</span>
          <span v-show="!multiComparison" class="vaf-label">Allele Fraction</span>
          <span v-show="multiComparison" class="vaf-label">Specimens</span>
          <div class="treatment-trial-labels">
            <span class="treatment-label">Therapies</span>
            <span class="trial-label">Trials</span>
          </div>
        </div>
        <div v-if="displayAlterationsFiltered.length" class="alterations">
          <div v-for="alteration in displayAlterationsFiltered" :key="alteration.id" class="variant-div">
            <VariantCard :alt="alteration" :hovered="multiCardHovered" :clicked="multiCardClicked" class="variant-card">
            </VariantCard>
          </div>
        </div>
        <div v-else class="msg-container">
          <div class="empty-state-msg" v-show="!spinnerOn">No alterations under this tier.</div>
        </div>
      </div>
      <div class="trials-container">
        <TreatmentsTrials :alterations="displayAlterationsFiltered" :loadingGenomics="loadingGenomics"
          :stillFetchingVariants="spinnerOn" :selected-tier="tierLabel" :multi-card-clicked="multiCardClicked">
        </TreatmentsTrials>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2024 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import VariantCard from './VariantCard.vue'
import TreatmentsTrials from './TreatmentsTrials.vue'
import { getTreatments, getTrials } from './getGenomicsData.js'
import { closeCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'
import { getReportFile } from '@/ux3/cards/ReportViewer/getReportFile.js'
import MultiSampleCard from './Multisample/MultiSampleCard.vue'
import { defaultPostalCode } from '@/settings.js'
import { loadAllGenomics } from './loadAllGenomics'

const controller = new AbortController()
const store = useStore()
const sampleId = computed(() => store.getters.session?.genomicsResults?.sampleId || '')
const accessionNum = computed(() => store.getters.session?.genomicsResults?.accessionNum || '')
const trialLinkUrl = computed(() => store.getters.session?.genomicsResults?.trialLink?.url || '')
const sampleIdStored = ref('')
const diagnosis = computed(() => store.getters.session?.genomicsResults?.diagnosis || '')
const specimenType = computed(() => store.getters.session?.genomicsResults?.specimenType || '')
const testType = computed(() => store.getters.session?.genomicsResults?.testType || '')
const uuid = computed(() => store.getters.session?.genomicsResults?.uuid || '')
const docType = computed(() => store.getters.session?.genomicsResults?.mimeType || '')
const multiComparison = computed(() => store.getters.session?.genomicsResults?.multiComparison || false)
const postalCode = computed(() => store.getters.session?.patient?.postalCode ||
  store.getters.session?.patient?.postal_code || defaultPostalCode || '')
const patient = computed(() => store.getters.session.patient)
const variants = ref([])
const fusions = ref([])
const rnaAnnotations = ref([])
const copyNumberVars = ref([])
const hlaTypes = ref([])
const alterations = ref([])
const displayAlterations = computed(() => {
  return tier1Selected.value ? displayAlterations1.value : tier2Selected.value ? displayAlterations2.value : displayAlterations3.value
})
const displayAlterationsFiltered = computed(() => {
  if (!multiComparison.value || multiCardClicked.value < 1) {
    return displayAlterations.value
  }
  return displayAlterations.value.filter((alter) => alter[`sample${multiCardClicked.value}`])
})
const displayAlterations1 = ref([])
const displayAlterations2 = ref([])
const displayAlterations3 = ref([])
const treatments = ref([])
const trials = ref([])
const loadingGenomics = ref(false)
const spinnerOn = ref(false)
const multiCardHovered = ref(-1)
const multiCardClicked = ref(-1)

const tmb = ref('No clinically significant TMB')
const msi = ref('No clinically significant MSI')

const tierIcon1 = ref('checked')
const tierIcon2 = ref('unchecked')
const tierIcon3 = ref('unchecked')

const tier1Selected = ref(true)
const tier2Selected = ref(false)
const tier3Selected = ref(false)
const tierLookup = { 1: tier1Selected, 2: tier2Selected, 3: tier3Selected }
const tierIconLookup = { 1: tierIcon1, 2: tierIcon2, 3: tierIcon3 }
const tierLabelLookup = { 1: 'Strong', 2: 'Potential', 3: 'Unknown' }

const tierLabel = ref(tierLabelLookup[1])
const clearTierTabs = () => {
  tier1Selected.value = false
  tier2Selected.value = false
  tier3Selected.value = false
}
const clearTierIcons = () => {
  tierIcon1.value = 'unchecked'
  tierIcon2.value = 'unchecked'
  tierIcon3.value = 'unchecked'
}

const tierButtonClick = async (number) => {
  clearTierIcons()
  clearTierTabs()
  tierLookup[number].value = true
  tierIconLookup[number].value = 'checked'
  tierLabel.value = tierLabelLookup[number]
  if (loadingGenomics.value) { return }
  loadingGenomics.value = true
  spinnerOn.value = true

  await getTreatmentsTrialsFirstAlteration()

  //get the rest
  await getTreatmentsTrialsParallel()
  loadingGenomics.value = false
  spinnerOn.value = false
}

const openFile = async () => {
  if (!uuid.value || !docType.value) { return }
  store.dispatch('showViewer', true)
  store.dispatch('reportDocType', docType.value)
  store.dispatch('reportDocument', await getReportFile(uuid.value, docType.value))
}

const onMultiComparisonCardClick = (number) => {
  if (multiCardClicked.value === number) { return multiCardClicked.value = -1 }
  multiCardClicked.value = number
}
const onMultiHoverOn = (number) => multiCardHovered.value = number
const onMultiHoverOff = () => multiCardHovered.value = -1

//Promise.allSettled approach. Faster but can flood annotation
const getTreatmentsTrialsParallel = async () => {
  const MAX_RETRIES = 1
  const promises = []
  const alters = []
  let retryCounter = 0
  const distance = '24000'
  const location = postalCode.value

  for (const alter of displayAlterations.value) {
    promises.push(fetchTreatments(alter))
    promises.push(fetchTrials(distance, location, alter))
    // this is a hack: two index values per alter so we grab the right alter on a retry
    // TODO: there is probably a better to do this mapping between failed promise and the
    // alteration we need for retry
    alters.push(alter)
    alters.push(alter)
  }

  const firstTreatmentsTrialsPromiseAll = async (array) => {
    const retryPromises = []
    await Promise.allSettled(array).then(results => {
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') {
          const alter = alters[index]
          console.error('Status unfulfilled on treatment or trial fetch: ', result.status, result.reason)
          retryPromises.push(fetchTreatments(alter))
          retryPromises.push(fetchTrials(distance, location, alter))
        }
      })
    })

    if (retryPromises.length && retryCounter < MAX_RETRIES) {
      console.log('we are doing a retry at the getTreatmentsTrialsParallel level')
      retryCounter += 1
      await firstTreatmentsTrialsPromiseAll(retryPromises)
    }
  }
  await firstTreatmentsTrialsPromiseAll(promises)
}

// const getTreatmentsTrialsSerial = async () => {
//   // TODO: replace with user input distance and patient location
//   const distance = '24000' //Note: leaving distance an empty string seems to be equivalent to 1000 mi
//   const location = postalCode.value
//   //skip the first because we've already done it
//   for (const alter of displayAlterations.value.slice(1, displayAlterations.value.length)) {
//     await fetchTreatments(alter)
//     await fetchTrials(distance, location, alter)
//   }
// }

const getTreatmentsTrialsFirstAlteration = async () => {
  const distance = '24000'
  const location = postalCode.value
  if (displayAlterations.value.length) {
    const alter = displayAlterations.value[0]
    await fetchTreatments(alter)
    await fetchTrials(distance, location, alter)
  }
}

const fetchTreatments = async (alter) => {
  const treatmentArgs = {
    controller,
    diagnosis: diagnosis.value,
    omicsType: alter.type,
    omicsValue: alter.value,
  }
  const results = await getTreatments(treatmentArgs)
  alter.treatmentsCount = parseInt(results.total, 10)
  treatments.value.push(results.rows)
  alter.treatmentRows = results.rows
}

const fetchTrials = async (distance, location, alter) => {
  const trialArgs = {
    age: patient.value.age,
    controller,
    diagnosis: diagnosis.value,
    distance,
    gender: patient.value.gender,
    location,
    omicsType: alter.type,
    omicsValue: alter.value,
    sampleId: alter.mostRecentAccessionNum,
  }
  const results = await getTrials(trialArgs)
  alter.trialsCount = parseInt(results.total, 10)
  trials.value.push(results.rows)
  alter.trialRows = results.rows
}

const loadAllGenomicsArgs = {
  accessionNum,
  alterations,
  controller,
  copyNumberVars,
  displayAlterations1,
  displayAlterations2,
  displayAlterations3,
  fusions,
  getTreatmentsTrialsFirstAlteration,
  getTreatmentsTrials: getTreatmentsTrialsParallel,
  hlaTypes,
  loadingGenomics,
  msi,
  multiComparison,
  rnaAnnotations,
  sampleId,
  spinnerOn,
  tierButtonClick,
  tmb,
  treatments,
  trials,
  variants,
}

onMounted(async () => {
  await loadAllGenomics(loadAllGenomicsArgs)
})

watch(sampleId, async () => {
  if (sampleId.value === sampleIdStored.value) { return }
  sampleIdStored.value = sampleId.value
  await loadAllGenomics(loadAllGenomicsArgs)
})

</script>

<style lang="scss" scoped>
a:link {
  text-decoration: none;
}

.multi-samples {
  display: flex;
  flex-direction: row;
  margin: 0 1rem 1rem;
}

.multi-samples-div {
  display: flex;
  justify-content: space-evenly;
  width: 100%;
}

.genomics-data {
  display: flex;
  flex-direction: row;
}

.trials-container {
  width: 26vw;
  flex-shrink: 1;
}

.variant-card {
  display: flex;
  height: fit-content;
  min-height: 2.5rem;
}

.alterations {
  height: calc(56vh - 7.5rem);
  width: calc(11.5vw + 11.75rem);
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.variant-div {
  width: fit-content;
}

.base-card {
  border-radius: 7px;
  box-shadow: 0 0 3px #b9b9b9;
  background-color: var(--White);
  overflow: hidden;
  position: relative;
}

.variants-container {
  font-size: 0.7rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: calc(11.5vw + 11.75rem);
  height: 80%;
  flex-shrink: 0;
  flex-grow: 3;
  overflow: auto;
  margin-right: 0.5rem;
  z-index: 100;
}

.table-headers {
  display: flex;
  gap: 2vw;
  font-weight: bold;
  border-bottom: solid;
  border-color: var(--Grey500);
  border-bottom-width: thin;
  padding-bottom: 0.25rem;

  span {
    color: var(--Grey500);
  }

  .treatment-trial-labels {
    display: flex;
    gap: 2rem;
  }
}

.table-headers-multi {
  gap: calc(5vw - 1.5rem);
}

.tier-tabs-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
}

.tier-text {
  margin-left: 5px;
  vertical-align: middle;
}

.significance-label {
  margin: 20px 0;
  color: var(--Black);
  width: 100%;
  text-align: center;
}

.tier-tabs {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: var(--Grey100);
  padding: 8px;
  border-radius: 5px;
  text-align: center;
  color: var(--Grey600);
  user-select: none;
  font-size: 90%;

  cursor: pointer;

  &:hover {
    color: var(--Black);
  }
}

.tier-tab-active {
  color: var(--Black)
}

.items {
  margin: 10px 5px 5px 5px;
  padding-left: 15px;
  font-size: 0.8rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.msi-tmb {
  margin-left: 0;
  padding-left: 0;
}

.view-report {
  padding-left: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .primary-outlined-button {
    display: flex;
    align-items: center;
    margin-left: 10px;

    span {
      padding-right: 8px;
      font-size: 20px;
    }
  }
}

.header-middle {
  display: flex;
  flex-direction: row;
}

.header-container {
  border-radius: 10px;
  background-color: var(--Grey100);
  overflow: auto;
  position: relative;
  text-align: left;
  margin: 1rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  height: calc(26vh - 6rem);
}

.header-top-first {
  flex-direction: row;
  margin-bottom: 5px;
}

.datum {
  margin: 5%;
  display: flex;
  flex-direction: column;
}

.location {
  color: var(--Grey500);
  margin: -2% 5% 2% 5%;
  display: flex;
  flex-direction: row;
  font-size: 90%;
}

.location-icon {
  padding-bottom: 3px;
}

.location-text {
  padding-top: 3px;
}

.specimen-type-text {
  font-weight: bold;
}

.tier-label {
  font-weight: bold;
}

.empty-state-msg {
  padding: 10px;
  color: var(--ErrorRedDark);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.msg-container {
  width: 100%;
}
</style>
